import { type TagProps } from '@loadsmart/miranda-react'

import { isString, plural } from 'core/utils/string'

import {
  AgnosticShipmentStatus,
  getNextStep,
  type AgnosticShipment,
  type ShipmentIdentifier,
} from '../domain/AgnosticShipment'
import { ShipmentNextStep, getAllIdentifiersCount, getPOIdentifiers } from '../domain/BaseShipment'
import { formatIdentifiers } from './baseShipmentPresenter'

export function formatAgnosticIdentifiers(compact: boolean, shipment: AgnosticShipment) {
  if (!compact) return formatIdentifiers(shipment)

  const po = getPOIdentifiers(shipment)
  if (!po) return []

  const allIdentifiersCount = getAllIdentifiersCount(shipment)
  const defaultLabel = formatIdentifiers([po]).slice(0, 1)

  if (allIdentifiersCount === 1) return defaultLabel

  return defaultLabel.concat(`+${allIdentifiersCount - 1}`)
}

export function formatNextStepLabel(shipment: AgnosticShipment) {
  const nextStep = getNextStep(shipment)

  switch (nextStep) {
    case ShipmentNextStep.AssignDriver:
      return 'Assign driver'
    case ShipmentNextStep.AddTruckingInfo:
      return 'Add trucking info'
    case ShipmentNextStep.AddTracking:
      return 'Add tracking'
    case ShipmentNextStep.CheckIn:
      return 'Check in'
    case ShipmentNextStep.CheckOut:
      return 'Check out'
    case ShipmentNextStep.UploadDocuments:
      return 'Upload documents'
  }
}

export function formatSectionCountSubtitle(count: number) {
  return `${count} ${plural('shipment', count)}`
}

export function formatStatusTagLabel(shipment: AgnosticShipment): string {
  switch (shipment.status) {
    case AgnosticShipmentStatus.AccountingReview:
      return 'Accounting review'
    case AgnosticShipmentStatus.Archived:
      return 'Archived'
    case AgnosticShipmentStatus.AtDelivery:
      return 'At delivery'
    case AgnosticShipmentStatus.AtPickup:
      return 'At pickup'
    case AgnosticShipmentStatus.Booked:
      return 'Booked'
    case AgnosticShipmentStatus.Canceled:
      return 'Canceled'
    case AgnosticShipmentStatus.Deleted:
      return 'Deleted'
    case AgnosticShipmentStatus.Delivered:
      return 'Delivered'
    case AgnosticShipmentStatus.EnRouteDelivery:
      return 'En route delivery'
    case AgnosticShipmentStatus.EnRoutePickup:
      return 'En route pickup'
    default:
      return ''
  }
}

export function formatStatusTagVariant(
  shipment: AgnosticShipment | AgnosticShipment['status']
): TagProps['variant'] {
  const status = isString(shipment) ? shipment : shipment.status
  switch (status) {
    case AgnosticShipmentStatus.Canceled:
    case AgnosticShipmentStatus.Deleted:
      return 'danger'
    case AgnosticShipmentStatus.AccountingReview:
    case AgnosticShipmentStatus.AtDelivery:
    case AgnosticShipmentStatus.AtPickup:
    case AgnosticShipmentStatus.Delivered:
    case AgnosticShipmentStatus.EnRouteDelivery:
    case AgnosticShipmentStatus.EnRoutePickup:
      return 'success'
    case AgnosticShipmentStatus.Archived:
    case AgnosticShipmentStatus.Booked:
      return 'neutral'
  }
}

export function formatShipmentIdentifier(value: ShipmentIdentifier) {
  switch (value) {
    case 'bol':
      return 'BOL'
    case 'container':
      return 'Container'
    case 'po':
      return 'PO'
    case 'reference':
      return 'Reference'
    case 'so':
      return 'SO'
  }
}
